import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { SceneService } from '../../services/scene.service';
import { Scene } from '../../models/scene';
import { Hotspot } from '../../models/hotspot';
import { Dimension } from '../../models/dimension';
import { stageTransition } from '../../animations/scene.animations';
import { Subscription } from 'rxjs/Rx';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { fadeBackground, toggleInfobox } from '../../animations/infobox.animations';

declare let cordova;

@Component({
	selector: 'app-stage',
	animations: [stageTransition, toggleInfobox, fadeBackground],
	templateUrl: './stage.component.html',
	styleUrls: ['./stage.component.scss']
})

/**
 * (c) it.x informationssysteme gmbh, Alle Rechte vorbehalten.
 *
 * Stage Component.
 */
export class StageComponent implements OnInit, OnDestroy
{
	/**
	 * Environment.
	 */
	public environment = environment;

	/**
	 * Cordova.
	 */
	public cordova;

	/**
	 * Scene.
	 */
	public scene: Scene;

	/**
	 * City image dimension.
	 */
	private cityDimension: Dimension = {
		'width': 2048,
		'height': 1365
	};

	/**
	 * Language.
	 */
	public language: string;

	/**
	 * Subscripton for the language.
	 */
	private languageSubscription: Subscription;

	/**
	 * ViewChild: Stage
	 */
	@ViewChild('stage')
	private stage: ElementRef;

	/**
	 * Is the infobox open?
	 */
	public infoboxIsOpen = false;

	/**
	 * Constructor.
	 */
	public constructor(
		private sceneService: SceneService,
		private router: Router,
		private route: ActivatedRoute,
		private translateService: TranslateService
	)
	{}

	/**
	 * Lifecycle-Hook: OnInit.
	 */
	public ngOnInit()
	{
		this.route.params.subscribe(params => {
			this.scene = this.sceneService.getScene(params['scene']);

			if (!params['scene'])
			{
				this.router.navigateByUrl('/portal/stage/' + this.scene.id);
			}

			this.scene = this.calculateSceneData(this.scene);
		});

		// Reset infobox (collapse) on route change (#31212)
		this.router.events.subscribe((event: RouterEvent) => {
			if (event instanceof NavigationEnd) {
				this.infoboxIsOpen = false;
			}
		});

		// Language.
		this.language = this.translateService.currentLang;
		this.languageSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
			this.language = event.lang;
		});

		// Cordova.
		if ((<any>window).cordova)
		{
			this.cordova = (<any>window).cordova;
		}
	}

	/**
	 * Lifecycle-Hook: OnDestroy.
	 */
	public ngOnDestroy()
	{
		this.languageSubscription.unsubscribe();
	}

	/**
	 * Calculate additional data for a specific scene.
	 */
	private calculateSceneData(scene: Scene): Scene
	{
		const updatedHotspots: Hotspot[] = [];

		const stageDimension: Dimension = {
			'width': this.stage.nativeElement.clientWidth,
			'height': this.stage.nativeElement.clientHeight
		};

		for (let hotspot of scene.hotspots)
		{
			hotspot = this.calculateHotspotData(hotspot, stageDimension);
			updatedHotspots.push(hotspot);
		}

		scene.hotspots = updatedHotspots;
		scene.style = {};

		let url = '';

		if (environment.webUrl !== '' && environment.expoBuild === false)
		{
			url = environment.webUrl;
		}
		else if (environment.webUrl === '' && environment.expoBuild === false)
		{
			url = cordova.file.dataDirectory;
		}

		if (scene.background && scene.background !== '')
		{
			scene.style = {
				'background-image': 'url(' + url + scene.background + ')'
			};
		}
		else if (scene.switch && scene.switch !== '')
		{
			scene.style = {
				'background-image': 'url(' + url + scene.switch + ')'
			};
		}

		return scene;
	}

	/**
	 * Calculate additional data for a specific hotspot.
	 */
	private calculateHotspotData(hotspot: Hotspot, stageDimension: Dimension): Hotspot
	{
		let cacluclatedHotspot = JSON.parse(JSON.stringify(hotspot));

		const imageDimension = this.cityDimension;
		const imageAspectRatio = imageDimension.width / imageDimension.height;
		const stageAspectRatio = stageDimension.width / stageDimension.height;

		let marginLeft = 0;
		let marginTop = 0;

		if (stageAspectRatio > imageAspectRatio)
		{
			// calculate margins
			marginLeft = (cacluclatedHotspot.position.x / imageDimension.width) * 100;
			marginTop = (cacluclatedHotspot.position.y / imageAspectRatio / imageDimension.height) * 100;
		}
		else
		{
			// calculate margins
			marginLeft = ((cacluclatedHotspot.position.x / (stageAspectRatio / imageAspectRatio)) / imageDimension.width) * 100;
			marginTop = (cacluclatedHotspot.position.y / stageAspectRatio / imageDimension.height) * 100;
		}

		// set margin
		cacluclatedHotspot.margin = {
			'margin-left': marginLeft,
			'margin-top': marginTop
		};

		// set styles
		cacluclatedHotspot.style = {
			'margin-left': marginLeft + '%',
			'margin-top': marginTop + '%'
		};

		return cacluclatedHotspot;
	}

	/**
	 * Checks if a hotspot is active.
	 */
	public isHotspotActive(id: number)
	{
		return this.sceneService.isHotspotActive(id);
	}

	/**
	 * Eventhandler: Resize.
	 */
	public onResize()
	{
		this.scene = this.calculateSceneData(this.scene);
	}

	/**
	 * Eventhandler: Click on a hotspot.
	 */
	public onClickHotspot(hotspot: Hotspot)
	{
		if (hotspot.link)
		{
			this.router.navigateByUrl('/portal/stage/' + hotspot.link);
		}
		else if (hotspot.content)
		{
			this.router.navigateByUrl('/portal/content/' + hotspot.content);
		}
	}

	/**
	 * Eventhandler: Click on a backlink.
	 */
	public onClickBack(id: number)
	{
		this.router.navigateByUrl('/portal/stage/' + id);
	}

	/**
	 * Returns the state of a scene.
	 */
	public getState(scene: Scene)
	{
		const currentScene = this.sceneService.getCurrentScene();

		if (!currentScene || currentScene.id !== scene.id)
		{
			this.sceneService.setCurrentScene(this.scene);
		}

		return {
			value: scene.state
		};
	}

	/**
	 * Eventhandler: Toggle infobox.
	 */
	public onToggleInfobox()
	{
		this.infoboxIsOpen = !this.infoboxIsOpen;
	}
}



// WEBPACK FOOTER //
// /var/data/workspace/hess/hess_smart_city_web/src/app/components/stage/stage.component.ts